import { TeamBase } from '../../shared/models/team-base';
import type { IGameRecords } from '../../shared/interfaces/IGameRecords';
import type { IGame } from '../../shared/interfaces/IGame';
import { RankMethod } from '../../shared/enums/rank-method';
import { NbaGameRecords } from './game-records';
import { Enumerable } from '../../shared/linq';

export class NbaTeam extends TeamBase {
  GamesBack: KnockoutObservable<number>;
  IsDivisionWinner: KnockoutObservable<boolean>;
  GroupedSchedule: KnockoutComputed<KnockoutObservable<IGame>[]>;
  subscribers: KnockoutSubscription[] = [];

  constructor(
    teamAbbreviation: string,
    regionName: string,
    teamName: string,
    conference: string,
    division: string,
    teamID: number,
    teamLogo?: string
  ) {
    super(teamAbbreviation, regionName, teamName, conference, division, teamID, teamLogo);

    this.GamesBack = ko.observable(0);

    this.IsDivisionWinner = ko.observable(false);

    this.GroupedSchedule = ko.computed(() => {
      this.FakeObs();
      let gameId = 1;
      const retArray: KnockoutObservable<IGame>[] = [];
      const groups = Enumerable.From(this.Schedule())
        .Where((p: KnockoutObservable<IGame>) => typeof p === 'function')
        .GroupBy((p: KnockoutObservable<IGame>) => p().week)
        .OrderBy((p: any) => p.Key())
        .ToArray();
      for (const group of groups) {
        const games = Enumerable.From(group.source)
          .OrderBy((p: KnockoutObservable<IGame>) => p().SortOrder())
          .ToArray() as KnockoutObservable<IGame>[];
        for (const game of games) {
          game().gameNum(gameId);
          gameId++;
          retArray.push(game);
        }
      }
      return retArray;
    });
  }

  protected override createGameRecords(): IGameRecords {
    return new NbaGameRecords(
      this.Wins,
      this.Losses,
      this.Ties,
      this.TeamName,
      this.FakeObs
    );
  }

  protected override createConferenceCss(): string {
    this.FakeObs();
    if (this.ConferenceRank() <= 6) {
      return 'in-post-season';
    } else if (this.ConferenceRank() <= 10) {
      return 'play-in';
    }
    return 'out-post-season';
  }

  protected override createRankMethodText(method?: RankMethod): string {
    let playin = '';
    if (this.ConferenceRank() > 6 && this.ConferenceRank() <= 10) {
      playin = 'Play In - ';
    }
    switch (method ?? this.RankMethod()) {
      case RankMethod.AllPCT:
        return playin + 'Pct vs League';
      case RankMethod.ConfPCT:
        return playin + 'Pct vs Conference';
      case RankMethod.DivPCT:
        return playin + 'Pct vs Div';
      case RankMethod.SOS:
        return playin + 'Strength of Schedule';
      case RankMethod.SOV:
        return playin + 'Strength of Victory';
      case RankMethod.DivTop_AllPCT:
        return playin + 'Top of Division - Pct vs League';
      case RankMethod.DivTop_DivPCT:
        return playin + 'Top of Division - Pct vs Div';
      case RankMethod.DivTop_ConfPCT:
        return playin + 'Top of Division - Pct vs Conference';
      case RankMethod.DivTop_SOS:
        return playin + 'Top of Division - Strength of Schedule';
      case RankMethod.DivTop_SOV:
        return playin + 'Top of Division - Strength of Victory';
      case RankMethod.DivTop_HTH:
        return playin + 'Top of Division - Head to Head';
      case RankMethod.HTH:
        return playin + 'Head-to-Head Tiebreaker';
      case RankMethod.CG:
        return playin + 'Common Games';
      case RankMethod.CoinFlip:
        return playin + 'Coin Flip';
      case RankMethod.CommonGames:
        return playin + 'Common Games';
      case RankMethod.CommonPct:
        return playin + 'Common Pct';
      case RankMethod.DivisionWinner:
        return playin + 'Division Winner';
      case RankMethod.DivisionWinnerMultiWay:
        return playin + 'Multi Way - Division Winner';
      case RankMethod.ConfPctFullBreakMultiWay:
        return playin + 'Multi Way - Full Break (Conference%)';
      case RankMethod.DivPctFullBreakMultiWay:
        return playin + 'Multi Way - Full Break (Division%)';
      case RankMethod.TwoWayConfPCT:
        return playin + 'Two Way - Conf%';
      case RankMethod.TwoWayDivPCT:
        return playin + 'Two Way - Div%';
      case RankMethod.HTHPartialBreakMultiWay:
        return playin + 'Multi Way - Partial Break (HTH)';
      case RankMethod.HTHTwoWay:
        return playin + 'Two Way - HTH';
      case RankMethod.HTHFullBreakMultiWay:
        return playin + 'Multi Way - HTH Full Break';
      case RankMethod.DivisionWinnerCoinFlip:
        return playin + 'Division Winner (Coin Flip)';
      case RankMethod.NonDivisionWinner:
        return playin + 'Non-Division Winner';
      case RankMethod.DivPctPartialBreakMultiWay:
        return playin + 'Multi Way - Partial Break (Div%)';
      default:
        return '';
    }
  }

  override initSubscribers(): void {
    const self = this;
    if (!self.isInit) {
      self.subscribers.push(
        self.Losses.subscribe((changes) => {
          changes.forEach((change) => {
            if (change.status === 'added') {
              self.Losses(
                Enumerable.From(self.Losses())
                  .Distinct((p: IGame) => p.gameGuid)
                  .ToArray()
              );
              self.Wins.remove((p: IGame) => p.gameGuid === change.value.gameGuid);
            }
          });
        }, null, 'arrayChange')
      );

      self.subscribers.push(
        self.Wins.subscribe((changes) => {
          changes.forEach((change) => {
            if (change.status === 'added') {
              self.Wins(
                Enumerable.From(self.Wins())
                  .Distinct((p: IGame) => p.gameGuid)
                  .ToArray()
              );
              self.Losses.remove((p: IGame) => p.gameGuid === change.value.gameGuid);
            }
          });
        }, null, 'arrayChange')
      );

      self.GameRecords.initSubscribers();
      self.isInit = true;
    }
  }

  override reset(): void {
    this.SOV(0);
    this.SOS(0);
    this.Wins([]);
    this.Losses([]);
    this.Ties([]);
    for (const subscriber of this.subscribers) {
      subscriber.dispose();
    }
    this.subscribers = [];
    for (const subscriber of (this.GameRecords as NbaGameRecords).subscribers) {
      subscriber.dispose();
    }
    (this.GameRecords as NbaGameRecords).subscribers = [];
    this.isInit = false;
    this.ConferenceRank(null);
    this.Schedule = ko.observable(new Array<KnockoutObservable<IGame>>());
    this.DraftOrderRankMethod(null);
    this.GamesBack(null);
  }
}
